import { useState } from "react";
import localization from "../../../../config/localization";
import { BonusConsts } from "../../models/constants/bonusConsts";
import {
  BonusTurnOverDetails,
  CommonBonusDetails,
  IncludeExcludeProvidersGamesDetails,
} from "../../models/responses";
import { formatUtcToLocalDateTime } from "../../../../utils/dateTimeHelper";
import TransactionCard from "../../../../components/ui/TransactionCard";
import { useLanguage } from "../../../../config/localization/LanguageProvider";
import { SupportedLanguages } from "../../../../models/constants/supportedLanguages";
import { Button } from "../../../../components/ui";
import {
  cancelDepositBonus,
  onIncludeExcludeProviderGame,
  retrieveCasinoBonusTurnOverDetail,
  retrieveExchangeBonusTurnOverDetails,
  retrieveSportsBonusTurnOverDetails,
} from "../../services/bonus.service";
import ProviderGamesDrawer from "../BonusComponents/ProviderGamesDrawer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faChevronUp,
  faIndianRupeeSign,
} from "@fortawesome/free-solid-svg-icons";
import toast from "react-hot-toast";
import MinimumOddsSection from "../BonusComponents/MinimumOddsSection";
import {
  ConfirmationModal,
  FeedbackModal as SuccessModel,
} from "../../../../components/ui/modals";
import { SPORTS_BET_NAMES } from "../../../../models/constants/sportsBetConsts";
import { DepositBonusType } from "../../models/constants/depositBonusType";
import BonusTurnoverDetails from "../BonusComponents/BonusTurnover";

interface DepositBonusProps {
  item: CommonBonusDetails;
  onCancel?: () => void;
}

export default function DepositBonus({ item, onCancel }: DepositBonusProps) {
  const { language } = useLanguage();
  const [isOpen, setOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const turnover = item.wageringrequired - item.wageringremaining;
  const [providerGames, setProviderGames] = useState<
    IncludeExcludeProvidersGamesDetails[]
  >([]);
  const [turnoverData, setTurnoverData] = useState<BonusTurnOverDetails[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState<string>();
  const bonusId = item.bonusid;
  const bonusWalletTransectionId = item.bonuswallettransactionid;
  const Status: Record<string, string> = {
    Consumed: localization.bonusconsumeddate,
    Cancelled: localization.bonuscanceldate,
    Expired: localization.bonusexpireddate,
    Active: localization.bonusexpiringdate,
  };

  const progresspercentage =
    Math.round(
      ((item.wageringrequired - item.wageringremaining) * 100) /
        item.wageringrequired,
    ) || 0;

  const Icons: Record<string, string> = {
    casino: "/icons/Bonus/ic-bonus-casino-vertical.svg",
    exchange: "/icons/Bonus/ic-bonus-exchange-vertical.svg",
    sports: "/icons/Bonus/ic-bonus-sports-vertical.svg",
  };

  const EXCHANGE_SUB_VERTICAL_ICONS: Record<
    number,
    { icon: string; alt: string }
  > = {
    1: { icon: "/icons/Bonus/ic-bonus-exchange-vertical.svg", alt: "Exchange" },
    2: {
      icon: "/icons/Bonus/ic-bonus-fancy-bet-sub-vertical.svg",
      alt: "FancyBet",
    },
    4: {
      icon: "/icons/Bonus/ic-bonus-premium-sub-vertical.svg",
      alt: "Premium",
    },
    5: {
      icon: "/icons/Bonus/ic-bonus-bookmaker-sub-vertical.svg",
      alt: "BookMaker",
    },
  };

  const getBetType = (value?: string | null): string => {
    if (!value || !value.trim()) {
      return "";
    }

    if (SPORTS_BET_NAMES[value]) {
      return SPORTS_BET_NAMES[value];
    }

    if (value.includes("/")) {
      const betTypeArr = value.split("/");

      if (betTypeArr.length === 2) {
        const [first, second] = betTypeArr;

        if (first === second && second !== "1") {
          return `${SPORTS_BET_NAMES["ComboBet"]} ${first} Bets`;
        }

        if (first === second && second === "1") {
          return "";
        }

        return `${SPORTS_BET_NAMES["SystemBet"]} ${second} Bets`;
      }
    }
    return "";
  };

  const handleProviderGamesClick = async () => {
    const details = await onIncludeExcludeProviderGame(item.bonusid);
    setProviderGames(details);
    setDrawerOpen(true);
  };

  const ShowBonusTurnover = async (
    bonuswallettransactionid: string,
    bonusid: string,
    verticalbonusname: string | null,
  ) => {
    const verticals = verticalbonusname?.split(",") || [];

    if (verticalbonusname === BonusConsts.CasinoVertical) {
      const casinoVertical = await retrieveCasinoBonusTurnOverDetail(
        bonusid,
        bonuswallettransactionid,
      );
      setTurnoverData(casinoVertical ?? []);
    } else if (verticalbonusname === BonusConsts.ExchangeVertical) {
      const exchangeVertical = await retrieveExchangeBonusTurnOverDetails(
        bonusid,
        bonuswallettransactionid,
      );
      setTurnoverData(exchangeVertical ?? []);
    } else if (
      verticals.includes(BonusConsts.SportsVertical) ||
      verticals.includes(BonusConsts.SportsIndia)
    ) {
      const sportsData = await retrieveSportsBonusTurnOverDetails(
        bonusid,
        bonuswallettransactionid,
      );
      setTurnoverData(sportsData ?? []);
    }
  };

  const cancelBonusConfirmation = (
    depositBonusStagString: string | null,
    DepositType: string,
  ) => {
    if (DepositType !== DepositBonusType.LadderDeposit) {
      setConfirmationMessage(localization.cancel_deposit_bonus_message);
    } else {
      const ladderStag = getLadderStag(depositBonusStagString);

      if (ladderStag.length === 2) {
        const remainingStag = Number(ladderStag[1]) - Number(ladderStag[0]);

        if (remainingStag === 0) {
          setConfirmationMessage(localization.cancel_deposit_bonus_message);
        } else {
          const message = localization.cancel_ladder_bonus
            .replace("{0}", ladderStag[1])
            .replace("{1}", `${remainingStag}`);

          setConfirmationMessage(message);
        }
      }
    }
    setIsConfirmModalOpen(true);
  };

  const getLadderStag = (value: string | null): string[] => {
    let firstArray = "";
    let secondArray = "";
    if (value && value.trim().length > 0) {
      value = value?.replace("(", "").replace(")", "") || "";
      if (value.includes("/")) {
        let ladderStepArr = value.split("/");
        if (ladderStepArr.length === 2) {
          firstArray = ladderStepArr[0];
          secondArray = ladderStepArr[1];
          return [firstArray, secondArray];
        }
      }
    }
    return [];
  };

  const handleCancelBonus = async () => {
    const result = await cancelDepositBonus(bonusId, bonusWalletTransectionId);
    if (result.isSuccess) {
      setIsConfirmModalOpen(false);
      setIsSuccessModalOpen(true);
    } else {
      setIsConfirmModalOpen(false);
      toast.error(result.message);
    }
  };
  return (
    <div key={item.bonuswallettransactionid}>
      <TransactionCard onToggleChange={(value) => setOpen(value)}>
        <div className="grid grid-rows-2 gap-3">
          <div className="flex items-center gap-2">
            <img
              src="/icons/ic-deposit.svg"
              alt="Deposit Bonus"
              className="size-6"
            />
            <span className="font-medium text-sm text-[#ECECFF]">
              {localization.deposit}
            </span>
          </div>
          <div className="font-medium text-lg">{item.bonusname}</div>
          <div>
            {item.verticalbonusname && (
              <div className="flex flex-wrap gap-1.5">
                {item.verticalbonusname.split(",").map((tag, index) => {
                  const name = tag.toLowerCase();

                  return (
                    Icons[name] && (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1.5 bg-[#1A1A1A] border border-[#2E2E2E] rounded-lg px-2.5 py-1"
                      >
                        <img src={Icons[name]} alt={tag} className="size-4" />
                        <span className="text-xs text-white">
                          {localization.getString(name)}
                        </span>
                      </span>
                    )
                  );
                })}
                {item.verticalbonusname.includes(
                  BonusConsts.ExchangeVertical,
                ) &&
                  item.exchangegameproviderids?.map((id) => {
                    const sub = EXCHANGE_SUB_VERTICAL_ICONS[id];
                    return sub && id !== 1 ? (
                      <span
                        key={`sub-${id}`}
                        className="inline-flex items-center gap-1.5 bg-[#1A1A1A] border border-[#353535] rounded-lg px-2.5 py-1"
                      >
                        <img src={sub.icon} alt={sub.alt} className="size-4" />
                        <span className="text-xs text-white">{sub.alt}</span>
                      </span>
                    ) : null;
                  })}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <img src="/icons/ic-bonus.svg" alt="icon" />
            <span className="font-medium text-lg">
              {`${item.bonusamount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              <FontAwesomeIcon icon={faIndianRupeeSign} size="xs" />
            </span>
            <div className="flex-1">
              <div className="h-2.5 bg-[#2E2E2E] rounded-full relative items-center">
                <div
                  className={`absolute top-1/2 -translate-y-1/2 h-4 rounded-full bg-gradient-to-r from-[#7A001C] to-[#D90031] flex items-center transition-all duration-500 ease-out min-w-[2rem] ${progresspercentage === 100 || progresspercentage <= 5 ? "justify-center" : "justify-end pr-3"}  ${item.status === BonusConsts.CancelledStatus || item.status === BonusConsts.ExpiredStatus ? "brightness-50" : ""}`}
                  style={{
                    width: `${Math.min(progresspercentage, 100)}%`,
                  }}
                >
                  <span className="text-white text-xs font-medium">
                    {progresspercentage}%
                  </span>
                </div>
              </div>
            </div>
          </div>
          <span className="border-t border-[#212121]"></span>

          <div>
            <span className="font-medium text-sm text-[#A9A9A9]">
              {localization.currentturnover}
            </span>
            <div className="flex items-center gap-2 float-right font-medium">
              <span>
                {`${turnover.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                <FontAwesomeIcon icon={faIndianRupeeSign} size="xs" />
              </span>
              <Button
                className="text-[#A9A9A9] hover:text-white transition no-underline opacity-70"
                onClick={async () => {
                  if (!expanded) {
                    await ShowBonusTurnover(
                      item.bonuswallettransactionid,
                      item.bonusid,
                      item.verticalbonusname,
                    );
                  }
                  setExpanded((prev) => !prev);
                }}
              >
                <FontAwesomeIcon
                  icon={expanded ? faChevronUp : faChevronDown}
                  className="h-4 w-4"
                />
              </Button>
            </div>
          </div>

          <span className="border-t border-[#212121]"></span>
          {expanded &&
            (turnoverData.length > 0 ? (
              turnoverData.map((data, index) => {
                return (
                  <div className="grid gap-3 mt-2 pt-2">
                    {item.verticalbonusname && (
                      <>
                        {index > 0 && (
                          <div className="border-t-4 border-dashed border-[#3C3C3C] my-3 mt-0"></div>
                        )}
                        <BonusTurnoverDetails
                          verticalbonusname={item.verticalbonusname}
                          data={data}
                          getBetType={getBetType}
                        />
                      </>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="flex justify-center">
                <span className="text-medium mt-5">
                  {localization.no_data_found}
                </span>
              </div>
            ))}

          {isOpen && (
            <div className="grid gap-4 pt-2">
              <div className="flex justify-between items-end">
                <div className="space-y-1">
                  <span className="text-sm md:text-base font-medium">
                    {`${item.wageringremaining.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    <FontAwesomeIcon icon={faIndianRupeeSign} size="xs" />
                  </span>
                  <p className="text-xs font-meduim text-[#A9A9A9]">
                    {localization.remainingturnover}
                  </p>
                </div>
                <div className="text-right space-y-1">
                  <span className="text-sm font-medium md:text-base">
                    {item.status === BonusConsts.CancelledStatus
                      ? formatUtcToLocalDateTime(item.modified, false)
                      : formatUtcToLocalDateTime(item.bonusexpiredate, false)}
                  </span>
                  <p className="text-xs font-meduim text-[#A9A9A9] text-left">
                    {Status[item.status]}
                  </p>
                </div>
              </div>
              {(item.verticalbonusname?.includes(
                BonusConsts.ExchangeVertical,
              ) ||
                item.verticalbonusname?.includes(
                  BonusConsts.SportsVertical,
                )) && (
                <MinimumOddsSection
                  verticalbonusname={item.verticalbonusname}
                  isindividualminimumoddsenabled={
                    item.isindividualminimumoddsenabled
                  }
                  exchangeminimumoddsjson={item.exchangeminimumoddsjson}
                  minimumodds={item.minimumodds}
                />
              )}
              <div className="text-sm md:text-base">
                <p>
                  {language === SupportedLanguages.Hindi
                    ? item.bonushindidescription
                    : item.bonusdescription}
                </p>
                <span className="font-meduim text-[#A9A9A9] text-xs">
                  {localization.bonusdescription}
                </span>
              </div>
              <div className="flex items-center justify-between gap-3">
                {item.status === BonusConsts.ActiveStatus ? (
                  <Button
                    className="flex gap-1 font-medium p-2 text-xs md:text-sm rounded bg-[#280401] text-[#FF6355] items-center"
                    onClick={() =>
                      cancelBonusConfirmation(
                        item.depositbonusstagstring,
                        item.depositType!,
                      )
                    }
                  >
                    <img
                      className="h-5 w-5"
                      src="/icons/ic-cancel-bonus.svg"
                      alt="icon"
                    />
                    {localization.cancelbonus}
                  </Button>
                ) : (
                  <div />
                )}
                {item.verticalbonusname?.includes(
                  BonusConsts.CasinoVertical,
                ) && (
                  <Button
                    onClick={handleProviderGamesClick}
                    className="flex items-center justify-between w-fit bg-[#001F3A] rounded-lg px-4 py-2.5 text-[#8ECAFF] text-xs font-medium"
                  >
                    <span>{localization.provider_and_games}</span>
                    <FontAwesomeIcon
                      icon={faChevronRight}
                      className="ml-3 text-xs"
                    />
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </TransactionCard>
      <ProviderGamesDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        details={providerGames}
      />
      {isConfirmModalOpen && (
        <ConfirmationModal
          isOpen={isConfirmModalOpen}
          title={
            confirmationMessage || localization.cancel_deposit_bonus_message
          }
          icon={"/icons/ic-info.svg"}
          blurColor="bg-[#FFE6CC]"
          confirmText="Yes"
          cancelText="No"
          onConfirm={handleCancelBonus}
          onCancel={() => setIsConfirmModalOpen(false)}
        />
      )}
      {isSuccessModalOpen && (
        <SuccessModel
          isOpen={isSuccessModalOpen}
          title={localization.cancel_deposit_bonus_success}
          icon={"/icons/ic-success-modal.svg"}
          blurColor="bg-[#D4FFDECC]"
          onClose={() => {
            setIsSuccessModalOpen(false);
            onCancel?.();
          }}
        />
      )}
    </div>
  );
}
